import { useQuery } from "@apollo/client";
import { Flex, List, Input, Spin, Tag, Button, Typography } from "antd";
import { useState, useEffect } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Navigate } from "react-router-dom";
import Layout from "../components/layout";
import Container from "../components/container";
import HeroScreen from "../components/HeroScreenV2/heroScreen";
import classes from "../components/HeroScreen/heroScreen.module.css";
import DoctorItem from "../components/doctors/doctorItem";
import { GET_ONCOLOGISTS_SEARCH } from "../components/doctors/oncologists.qql";
import classesSearch from "../components/doctors/searchDoctor.module.css";
import { isSignedInState } from "../../store/slices/user";
import { useAppSelector } from "../../store";
import { appRoutes } from "../../constants/routes";

const { Title } = Typography;

const Experts = () => {
  const [surname, setSurname] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [skills, setSkills] = useState<string[]>([]);
  const isSignedIn = useAppSelector(isSignedInState);

  if (isSignedIn) {
    return <Navigate to={appRoutes.appointments} />;
  }

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSurname(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, loading } = useQuery(GET_ONCOLOGISTS_SEARCH, {
    fetchPolicy: "no-cache",
    variables: {
      surname: surname || null,
      skills: selectedSkills.length > 0 ? selectedSkills : null
    }
  });

  const handleSkillToggle = (skill: string) => {
    setSelectedSkills(prev => (prev.includes(skill)
        ? prev.filter(s => s !== skill)
        : [...prev, skill]));
  };

  const clearFilters = () => {
    setSearchInput("");
    setSelectedSkills([]);
  };

  useEffect(() => {
    if (data?.skills) {
      setSkills(data.skills.map((skill: { name: string }) => skill.name));
    }
  }, [data]);

  return (
    <Layout>
      <HeroScreen imgSrc="/landing/home/hero-iphone.png" noBackground>
        <h1 className="heroTitle desktop_text-[40px]">
          Scegli il tuo Psiconcologo<br/>
          e prenota subito una consulenza.
        </h1>
        <Flex
          vertical
          gap={16}
          className={`${classes.text} text-sm max-w-[515px]`}
        >
          <p>
            Clicca sul tuo preferito, ricercalo per nome o seleziona
            l&apos;ambito di specializzazione.
            Se preferisci invece un supporto personalizzato<br/>
            <strong>Inizia il quesionario</strong>
          </p>
        </Flex>
      </HeroScreen>
      <Container>
        <div className="max-w-[1053px] w-full mx-auto relative z-10">
          <div className="text-center mb-8">
            <div className="mb-4">
              <Input.Search
                placeholder="Cerca"
                size="large"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onSearch={(query: string) => setSearchInput(query)}
                className={classesSearch.search}
                enterButton={<Button type="text" size="small" icon={<SearchOutlined />} shape="circle" />}
                allowClear
              />
            </div>

            {/* Skill Tags */}
            <div className="mb-6">
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {skills.map((skill: string) => (
                  <Tag.CheckableTag
                    key={skill}
                    checked={selectedSkills.includes(skill)}
                    onChange={() => handleSkillToggle(skill)}
                    style={{
                      padding: "4px 12px",
                      fontSize: "14px",
                      textTransform: "uppercase",
                      fontWeight: "bold",
                      borderRadius: "20px",
                      backgroundColor: "#EDF0F7",
                      border: 0,
                      color: selectedSkills.includes(skill) ? "#0D55C0" : "#59A4EC",
                      cursor: "pointer",
                      transition: "all 0.3s ease"
                    }}
                  >
                    {skill}
                  </Tag.CheckableTag>
                ))}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="mt-8">
            {loading ? (
              <div className="text-center py-8">
                <Spin size="large" />
                <p className="mt-4 text-gray-500">Ricerca in corso...</p>
              </div>
            ) : null}
            {data?.oncologistsSearch?.length && !loading ? (
              <List
                grid={{ gutter: 16, column: 2, xs: 1, sm: 1, md: 1, lg: 2 }}
                dataSource={data.oncologistsSearch}
                renderItem={(doctor: any) => (
                  <List.Item key={doctor.id}>
                    <DoctorItem doctor={doctor} />
                  </List.Item>
                  )}
              />
            ) : null}
            {data?.oncologistsSearch?.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🔍</div>
                <Title level={4} className="text-gray-500">
                  Nessun risultato trovato
                </Title>
                <p className="text-gray-400 mt-2">
                  Prova a modificare i criteri di ricerca o i filtri
                </p>
                <Button type="primary" onClick={clearFilters} className="mt-4">
                  Cancella filtri
                </Button>
              </div>
            )}
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default Experts;
